import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname.startsWith("/admin") && window.location.pathname !== "/admin") {
      localStorage.removeItem("admin_token");
      window.location.href = "/admin";
    }
    return Promise.reject(err);
  }
);

export function apiError(err) {
  const d = err.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return err.message || "Xəta baş verdi";
}

export const adminApi = {
  login: (username, password) => api.post("/admin/auth/login", { username, password }),
  me: () => api.get("/admin/auth/me"),
  listAdmins: () => api.get("/admin/auth/admins"),
  createAdmin: (data) => api.post("/admin/auth/admins", data),
  deleteAdmin: (id) => api.delete(`/admin/auth/admins/${id}`),

  liveChannels: () => api.get("/admin/live-channels"),
  aiImage: (prompt) => api.post("/admin/banners/ai-image", { prompt }, { timeout: 120000 }),
  aiText: (prompt) => api.post("/admin/banners/ai-text", { prompt }, { timeout: 60000 }),

  banners: () => api.get("/admin/banners"),
  createBanner: (data) => api.post("/admin/banners", data),
  updateBanner: (id, data) => api.put(`/admin/banners/${id}`, data),
  deleteBanner: (id) => api.delete(`/admin/banners/${id}`),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },

  wgChannels: () => api.get("/admin/wg/channels"),
  createWgChannel: (data) => api.post("/admin/wg/channels", data),
  updateWgChannel: (id, data) => api.put(`/admin/wg/channels/${id}`, data),
  deleteWgChannel: (id) => api.delete(`/admin/wg/channels/${id}`),
  seedWgDefaults: () => api.post("/admin/wg/seed-defaults"),
  wgSettings: () => api.get("/admin/wg/settings"),
  updateWgSettings: (data) => api.put("/admin/wg/settings", data),
  wgGuideStatus: () => api.get("/admin/wg/guide-status"),
  importWgGuide: () => api.post("/admin/wg/import-guide"),

  epgSources: () => api.get("/admin/epg/sources"),
  createEpgSource: (data) => api.post("/admin/epg/sources", data),
  updateEpgSource: (id, data) => api.put(`/admin/epg/sources/${id}`, data),
  deleteEpgSource: (id) => api.delete(`/admin/epg/sources/${id}`),
  fetchEpgSource: (id) => api.post(`/admin/epg/sources/${id}/fetch`),
  fetchAllEpgSources: () => api.post("/admin/epg/sources/fetch-all"),
  autoMapEpg: (remap = false) => api.post(`/admin/epg/auto-map?remap=${remap}`),
  epgHubStatus: () => api.get("/admin/epg/hub-status"),
  searchFeedChannels: (q, sourceId = "") => api.get(`/admin/epg/feed-channels?q=${encodeURIComponent(q)}&source_id=${sourceId}`),
  setChannelMapping: (id, data) => api.put(`/admin/epg/channels/${id}/mapping`, data),
  clearChannelMapping: (id) => api.delete(`/admin/epg/channels/${id}/mapping`),

  epgChannels: () => api.get("/admin/epg/channels"),
  importProviderChannels: () => api.post("/admin/epg/import-provider", null, { timeout: 60000 }),
  findSite: (id) => api.post(`/admin/epg/find-site/${id}`, null, { timeout: 120000 }),
  findSitesAll: () => api.post("/admin/epg/find-sites-all"),
  findSitesStatus: () => api.get("/admin/epg/find-sites-status"),
  createEpgChannel: (data) => api.post("/admin/epg/channels", data),
  updateEpgChannel: (id, data) => api.put(`/admin/epg/channels/${id}`, data),
  deleteEpgChannel: (id) => api.delete(`/admin/epg/channels/${id}`),
  uploadChannelList: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/admin/epg/channels/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  parseChannel: (id) => api.post(`/admin/epg/parse/${id}`),
  parseAll: () => api.post("/admin/epg/parse-all"),
  channelPrograms: (id) => api.get(`/admin/epg/programs/${id}`),

  domains: () => api.get("/admin/domains"),
  createDomain: (data) => api.post("/admin/domains", data),
  updateDomain: (id, data) => api.put(`/admin/domains/${id}`, data),
  deleteDomain: (id) => api.delete(`/admin/domains/${id}`),

  versions: () => api.get("/admin/versions"),
  upsertVersion: (data) => api.post("/admin/versions", data),
  deleteVersion: (id) => api.delete(`/admin/versions/${id}`),

  pushStats: () => api.get("/admin/push/stats"),
  pushAnalytics: () => api.get("/admin/push/analytics"),
  cancelScheduledPush: (id) => api.delete(`/admin/push/scheduled/${id}`),
  sendPush: (data) => api.post("/admin/push/send", data),
};

export default adminApi;
